import Grid from '../components/grid'
import Container from '../components/ui/container'
import Text from '../components/ui/text'
import { Asset } from '../v2/components/Asset'
import { CMSLink } from '../v2/components/CMSLink'
import Divider from './Divider'
import { Asset as AssetType, Page } from '@telesoft/types/payload-types'
import cn from 'classnames'
import React, { ReactElement } from 'react'
import { RichText } from '../v2/components/RichText'
import { AssetProps } from '../v2/components/Asset/types'
import { Video } from '../v2/components/Asset/Video'

function renderImage(image: AssetType) {
  return <Asset fill resource={image} imgClassName="object-cover object-center" />
}

export type CustomVideo = {
  video: {
    createdAt: string
    filename: string
    filesize: number
    id: string
    mimeType: string
    updatedAt: string
    url: string
  }
} & AssetProps

function renderVideo(video: CustomVideo) {
  return (
    <div className="relative max-w-max w-full overflow-hidden">
      <Video {...video} width={560} height={315} />
    </div>
  )
}

function renderText(obj: any) {
  if (!obj || !Object.prototype.hasOwnProperty.call(obj, 'options')) {
    return ''
  }

  const { theme, isSubtitle, subTitle, titleColour, textAlignment } = obj.options
  const renderTitle = () => {
    if (Object.prototype.hasOwnProperty.call(obj, 'title')) {
      return (
        <div className="">
          {isSubtitle && (
            <h4
              className={cn('mb-3', {
                'text-white': theme === 'light',
                'text-black': theme === 'dark',
              })}
            >
              {subTitle}
            </h4>
          )}
          <Text
            variant="subHeading"
            className={cn('pb-4 md:pb-6 lg:pb-8 w-full', {
              'text-telesoft': titleColour === 'blue',
              'text-white': titleColour === 'light',
              'text-black': titleColour === 'black',
            })}
          >
            {obj.title}
          </Text>
        </div>
      )
    }
  }

  return (
    <>
      <div
        className={cn(`mx-auto flex flex-col justify-center`, {
          'text-center items-center': textAlignment === 'text-center',
          'text-left items-start': textAlignment === 'text-left',
        })}
      >
        {renderTitle()}
        <RichText
          content={obj.description}
          className={cn('max-w-4xl', {
            'text-white': theme === 'light',
            'text-black': theme === 'dark',
          })}
        />
      </div>
    </>
  )
}

function renderButton(obj: any) {
  const { link } = obj
  return <CMSLink {...link} className="content-center mt-0 mb-8" />
}

export type FullWidthDynamicBlockType = Extract<
  Page['blocks'][0],
  { blockType: 'full-width-dynamic-block' }
>

function FullWidthDynamicBlock(block: FullWidthDynamicBlockType): ReactElement {
  const { columns, background, dividerBottom, dividerTop, links } = block

  const gradient = () => {
    if (background && background.length > 0) {
      if (Object.prototype.hasOwnProperty.call(background[0], 'gradient')) {
        const gradient = background[0].gradient
        if (gradient === 'none') {
          return ''
        } else if (gradient === 'blue-to-transparent') {
          return 'mix-blend-soft-light bg-gradient-to-b from-telesoft to-transparent'
        } else if (gradient === 'transparent-to-blue') {
          return 'mix-blend-soft-light bg-gradient-to-b from-transparent to-telesoft'
        } else if (gradient === 'black-to-transparent') {
          return 'mix-blend-soft-light bg-gradient-to-b from-black to-transparent'
        } else if (gradient === 'transparent-to-black') {
          return 'mix-blend-soft-light bg-gradient-to-b from-transparent to-black'
        }
      }
    }
    return ''
  }

  const backgroundColour = () => {
    if (background && background.length > 0) {
      if (background[0].backgroundColour!.length > 0) {
        const colour = background[0].backgroundColour![0]
        if (Object.prototype.hasOwnProperty.call(colour, 'color')) {
          return colour.color
        } else {
          return 'transparent'
        }
      }
    }
    return 'transparent'
  }

  const backgroundImage = () => {
    if (background && background.length > 0) {
      if (Object.prototype.hasOwnProperty.call(background[0], 'backgroundImage')) {
        if (background[0].backgroundImage!.length > 0) {
          const image = background[0].backgroundImage![0].bgImage as AssetType
          return renderImage(image)
        }
      }
    }
  }

  const borderTop = () => {
    if (dividerTop === 'none') {
      return ''
    } else if (dividerTop === 'blackline') {
      return <Divider withLine={true} />
    } else if (dividerTop === 'noline') {
      return <Divider withLine={false} />
    }
  }

  const borderBottom = () => {
    if (dividerBottom === 'none') {
      return ''
    } else if (dividerBottom === 'blackline') {
      return <Divider withLine={true} />
    } else if (dividerBottom === 'noline') {
      return <Divider withLine={false} />
    }
  }

  const renderContent = () => {
    if (columns && columns.length === 1) {
      return (
        <>
          <div className="relative col-span-full min-h-[240px] lg:min-h-[300px] z-20 mb-8 flex items-center justify-center">
            {renderColumn()}
          </div>
        </>
      )
    } else {
      return <>{renderColumn(true)}</>
    }
  }

  const renderColumn = (split = false) => {
    return (
      columns &&
      columns.map((column: any, i: number) => {
        if (column.activatedContent === 'image') {
          if (column.images.length <= 0) {
            return (
              <div
                key={`items-map-${i}`}
                className="relative col-span-full lg:col-span-6 min-h-[240px] lg:max-w-[560px] z-20"
              ></div>
            )
          }
          const { image } = column.images[0]
          if (split) {
            return (
              <div
                key={`items-map-${i}`}
                className="relative col-span-full lg:col-span-6 min-h-[240px] lg:min-h-[300px] z-20"
              >
                {renderImage(image)}
              </div>
            )
          } else {
            return <div key={`items-map-${i}`}>{renderImage(image)}</div>
          }
        } else if (column.activatedContent === 'video') {
          if (column.videos.length <= 0) {
            return (
              <div
                key={`items-map-${i}`}
                className="mx-auto md:col-span-4 lg:col-span-6 z-20 flex flex-col justify-center text-left items-start"
              ></div>
            )
          }
          const video = column.videos[0]
          if (split) {
            return (
              <div
                key={`items-map-${i}`}
                className="mx-auto w-full col-span-full lg:col-span-6 z-20 flex flex-col justify-center text-left items-start"
              >
                {renderVideo(video)}
              </div>
            )
          } else {
            return <div key={`items-map-${i}`}>{renderVideo(video)}</div>
          }
        } else if (column.activatedContent === 'text') {
          if (column.texts.length <= 0) {
            return (
              <div
                key={`items-map-${i}`}
                className="mx-auto md:col-span-4 lg:col-span-6 z-20 flex flex-col justify-center text-left items-start"
              ></div>
            )
          }
          const text = column.texts[0]
          if (split) {
            return (
              <div
                key={`items-map-${i}`}
                className="mx-auto col-span-full lg:col-span-6 z-20 mb-8 flex flex-col justify-center text-left items-start"
              >
                {renderText(text)}
              </div>
            )
          } else {
            return <div key={`items-map-${i}`}>{renderText(text)}</div>
          }
        }
      })
    )
  }

  const renderLinks = () => {
    return (
      links &&
      links.map((link: any, i: number) => {
        return (
          <div key={`items-map-${i}`} className="inline-block mr-4">
            {renderButton(link)}
          </div>
        )
      })
    )
  }

  return (
    <React.Fragment>
      {borderTop()}
      <div className="mx-auto px-4 relative md:px-8 2xl:px-16 py-6 lg:py-7 xl:py-8">
        <Container>
          <div
            style={{ backgroundColor: `${backgroundColour()}` }}
            className={`absolute inset-0 z-10 ${gradient()}`}
          />
          <div className={`absolute inset-0`}>{backgroundImage()}</div>
          <Grid>
            {renderContent()}
            <div className="mx-auto col-span-full z-20 flex-col justify-center text-left items-start inline-block -mt-8">
              {renderLinks()}
            </div>
          </Grid>
        </Container>
      </div>
      {borderBottom()}
    </React.Fragment>
  )
}

export default FullWidthDynamicBlock
